/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';
import {
  getPayloadBufDecoder,
  getPayloadBufEncoder,
  type PayloadBuf,
  type PayloadBufArgs,
} from '../types';

export const LAUNCH_DISCRIMINATOR = new Uint8Array([
  144, 51, 51, 163, 206, 85, 213, 38,
]);

export function getLaunchDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(LAUNCH_DISCRIMINATOR);
}

export type Launch = {
  discriminator: ReadonlyUint8Array;
  authority: Address;
  namespace: Address;
  launchId: ReadonlyUint8Array;
  phase: number;
  bump: number;
  launchAuthorityBump: number;
  pad0: ReadonlyUint8Array;
  baseMint: Address;
  quoteMint: Address;
  baseVault: Address;
  quoteVault: Address;
  baseTotalSupply: bigint;
  baseForDistribution: bigint;
  baseForLiquidity: bigint;
  baseForCurve: bigint;
  curveVirtualBase: bigint;
  curveVirtualQuote: bigint;
  swapFeeBps: number;
  pad1: ReadonlyUint8Array;
  allowBuy: number;
  allowSell: number;
  pad2: ReadonlyUint8Array;
  hookProgram: Address;
  hookFlags: number;
  pad3: ReadonlyUint8Array;
  hookPayload: PayloadBuf;
  migratorProgram: Address;
  migratorInitPayload: PayloadBuf;
  migratorMigratePayload: PayloadBuf;
  curveKind: number;
  swapLock: number;
  vestingEnabled: number;
  pad4: ReadonlyUint8Array;
  curveParams: PayloadBuf;
  createdAt: bigint;
  reserved: ReadonlyUint8Array;
};

export type LaunchArgs = {
  authority: Address;
  namespace: Address;
  launchId: ReadonlyUint8Array;
  phase: number;
  bump: number;
  launchAuthorityBump: number;
  pad0: ReadonlyUint8Array;
  baseMint: Address;
  quoteMint: Address;
  baseVault: Address;
  quoteVault: Address;
  baseTotalSupply: number | bigint;
  baseForDistribution: number | bigint;
  baseForLiquidity: number | bigint;
  baseForCurve: number | bigint;
  curveVirtualBase: number | bigint;
  curveVirtualQuote: number | bigint;
  swapFeeBps: number;
  pad1: ReadonlyUint8Array;
  allowBuy: number;
  allowSell: number;
  pad2: ReadonlyUint8Array;
  hookProgram: Address;
  hookFlags: number;
  pad3: ReadonlyUint8Array;
  hookPayload: PayloadBufArgs;
  migratorProgram: Address;
  migratorInitPayload: PayloadBufArgs;
  migratorMigratePayload: PayloadBufArgs;
  curveKind: number;
  swapLock: number;
  vestingEnabled: number;
  pad4: ReadonlyUint8Array;
  curveParams: PayloadBufArgs;
  createdAt: number | bigint;
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link LaunchArgs} account data. */
export function getLaunchEncoder(): FixedSizeEncoder<LaunchArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['authority', getAddressEncoder()],
      ['namespace', getAddressEncoder()],
      ['launchId', fixEncoderSize(getBytesEncoder(), 32)],
      ['phase', getU8Encoder()],
      ['bump', getU8Encoder()],
      ['launchAuthorityBump', getU8Encoder()],
      ['pad0', fixEncoderSize(getBytesEncoder(), 5)],
      ['baseMint', getAddressEncoder()],
      ['quoteMint', getAddressEncoder()],
      ['baseVault', getAddressEncoder()],
      ['quoteVault', getAddressEncoder()],
      ['baseTotalSupply', getU64Encoder()],
      ['baseForDistribution', getU64Encoder()],
      ['baseForLiquidity', getU64Encoder()],
      ['baseForCurve', getU64Encoder()],
      ['curveVirtualBase', getU64Encoder()],
      ['curveVirtualQuote', getU64Encoder()],
      ['swapFeeBps', getU16Encoder()],
      ['pad1', fixEncoderSize(getBytesEncoder(), 6)],
      ['allowBuy', getU8Encoder()],
      ['allowSell', getU8Encoder()],
      ['pad2', fixEncoderSize(getBytesEncoder(), 6)],
      ['hookProgram', getAddressEncoder()],
      ['hookFlags', getU32Encoder()],
      ['pad3', fixEncoderSize(getBytesEncoder(), 4)],
      ['hookPayload', getPayloadBufEncoder()],
      ['migratorProgram', getAddressEncoder()],
      ['migratorInitPayload', getPayloadBufEncoder()],
      ['migratorMigratePayload', getPayloadBufEncoder()],
      ['curveKind', getU8Encoder()],
      ['swapLock', getU8Encoder()],
      ['vestingEnabled', getU8Encoder()],
      ['pad4', fixEncoderSize(getBytesEncoder(), 5)],
      ['curveParams', getPayloadBufEncoder()],
      ['createdAt', getI64Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 64)],
    ]),
    (value) => ({ ...value, discriminator: LAUNCH_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link Launch} account data. */
export function getLaunchDecoder(): FixedSizeDecoder<Launch> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['authority', getAddressDecoder()],
    ['namespace', getAddressDecoder()],
    ['launchId', fixDecoderSize(getBytesDecoder(), 32)],
    ['phase', getU8Decoder()],
    ['bump', getU8Decoder()],
    ['launchAuthorityBump', getU8Decoder()],
    ['pad0', fixDecoderSize(getBytesDecoder(), 5)],
    ['baseMint', getAddressDecoder()],
    ['quoteMint', getAddressDecoder()],
    ['baseVault', getAddressDecoder()],
    ['quoteVault', getAddressDecoder()],
    ['baseTotalSupply', getU64Decoder()],
    ['baseForDistribution', getU64Decoder()],
    ['baseForLiquidity', getU64Decoder()],
    ['baseForCurve', getU64Decoder()],
    ['curveVirtualBase', getU64Decoder()],
    ['curveVirtualQuote', getU64Decoder()],
    ['swapFeeBps', getU16Decoder()],
    ['pad1', fixDecoderSize(getBytesDecoder(), 6)],
    ['allowBuy', getU8Decoder()],
    ['allowSell', getU8Decoder()],
    ['pad2', fixDecoderSize(getBytesDecoder(), 6)],
    ['hookProgram', getAddressDecoder()],
    ['hookFlags', getU32Decoder()],
    ['pad3', fixDecoderSize(getBytesDecoder(), 4)],
    ['hookPayload', getPayloadBufDecoder()],
    ['migratorProgram', getAddressDecoder()],
    ['migratorInitPayload', getPayloadBufDecoder()],
    ['migratorMigratePayload', getPayloadBufDecoder()],
    ['curveKind', getU8Decoder()],
    ['swapLock', getU8Decoder()],
    ['vestingEnabled', getU8Decoder()],
    ['pad4', fixDecoderSize(getBytesDecoder(), 5)],
    ['curveParams', getPayloadBufDecoder()],
    ['createdAt', getI64Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 64)],
  ]);
}

/** Gets the codec for {@link Launch} account data. */
export function getLaunchCodec(): FixedSizeCodec<LaunchArgs, Launch> {
  return combineCodec(getLaunchEncoder(), getLaunchDecoder());
}

export function decodeLaunch<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<Launch, TAddress>;
export function decodeLaunch<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<Launch, TAddress>;
export function decodeLaunch<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<Launch, TAddress> | MaybeAccount<Launch, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getLaunchDecoder(),
  );
}

export async function fetchLaunch<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<Launch, TAddress>> {
  const maybeAccount = await fetchMaybeLaunch(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeLaunch<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<Launch, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeLaunch(maybeAccount);
}

export async function fetchAllLaunch(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<Launch>[]> {
  const maybeAccounts = await fetchAllMaybeLaunch(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeLaunch(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<Launch>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeLaunch(maybeAccount));
}

export function getLaunchSize(): number {
  return 1488;
}
